import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Table, Button, Form, Badge, Spinner, Alert } from "react-bootstrap";
import api from "../../api";
import { toast } from "react-toastify";
import "./TaskWorkflowEditor.css";

// Phase 2B. Statuses/transitions editor for one workflow — §27. Deliberately
// plain tables + selects, no visual node-graph library, per the task spec.
export default function TaskWorkflowEditor() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [workflow, setWorkflow] = useState(null);
  const [allStatuses, setAllStatuses] = useState([]);
  const [loading, setLoading] = useState(false);

  const [includedStatuses, setIncludedStatuses] = useState([]); // {status_id, code, name, display_order, is_initial, is_terminal, is_required}
  const [savingStatuses, setSavingStatuses] = useState(false);

  const [transitions, setTransitions] = useState([]); // {from_status_id, to_status_id, transition_code, transition_name, required_permission_module, required_permission_action, requires_remark, requires_assignment, is_reopen_transition, display_order}
  const [savingTransitions, setSavingTransitions] = useState(false);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const load = async () => {
    try {
      setLoading(true);
      const [wfRes, statusRes] = await Promise.all([
        api.get(`/task-workflows/${id}`),
        api.get("/task-statuses", { params: { is_active: 1, pageSize: 200, sortField: "display_order" } }),
      ]);
      const wf = wfRes.data?.data;
      setWorkflow(wf);
      setAllStatuses(Array.isArray(statusRes.data?.data) ? statusRes.data.data : []);

      setIncludedStatuses(
        (wf?.statuses || []).map((s) => ({
          status_id: s.status_id,
          code: s.code,
          name: s.name,
          display_order: s.display_order ?? 0,
          is_initial: !!s.is_initial,
          is_terminal: !!s.is_terminal,
          is_required: !!s.is_required,
        }))
      );
      setTransitions(
        (wf?.transitions || []).map((t) => ({
          from_status_id: t.from_status_id,
          to_status_id: t.to_status_id,
          transition_code: t.transition_code,
          transition_name: t.transition_name,
          required_permission_module: t.required_permission_module || "",
          required_permission_action: t.required_permission_action || "",
          requires_remark: !!t.requires_remark,
          requires_assignment: !!t.requires_assignment,
          is_reopen_transition: !!t.is_reopen_transition,
          display_order: t.display_order ?? 0,
        }))
      );
    } catch (err) {
      console.error("Load workflow editor error:", err);
      toast.error(err.response?.data?.message || "Failed to load workflow");
    } finally {
      setLoading(false);
    }
  };

  const availableStatuses = useMemo(
    () => allStatuses.filter((s) => !includedStatuses.some((i) => i.status_id === s.id)),
    [allStatuses, includedStatuses]
  );

  // CLOSED/CANCELLED must not be offered as a NEW transition's from/to
  // status, even if an older workflow still has them in its included-status
  // list (e.g. seeded before they were deactivated) -- the "Included
  // Statuses" section above still lists them so an admin can see/remove
  // them, this only narrows the transition-endpoint pickers. `allStatuses`
  // is already fetched with `is_active: 1` (see load() above), so this is
  // additionally a safety net for any workflow whose status membership
  // predates that deactivation.
  const LEGACY_NON_SELECTABLE_CODES = ["CLOSED", "CANCELLED"];
  const transitionEndpointStatuses = useMemo(
    () => includedStatuses.filter((s) => !LEGACY_NON_SELECTABLE_CODES.includes(String(s.code || "").toUpperCase())),
    [includedStatuses]
  );

  const toggleIncludeStatus = (status, include) => {
    if (include) {
      setIncludedStatuses((prev) => [
        ...prev,
        {
          status_id: status.id,
          code: status.code,
          name: status.name,
          display_order: prev.length,
          is_initial: prev.length === 0,
          is_terminal: false,
          is_required: true,
        },
      ]);
    } else {
      const stillUsed = transitions.some((t) => t.from_status_id === status.id || t.to_status_id === status.id);
      if (stillUsed) {
        toast.warning("Remove any transitions that reference this status first.");
        return;
      }
      setIncludedStatuses((prev) => prev.filter((i) => i.status_id !== status.id));
    }
  };

  const updateIncludedStatus = (statusId, field, value) => {
    setIncludedStatuses((prev) =>
      prev.map((s) => {
        if (field === "is_initial") {
          return { ...s, is_initial: s.status_id === statusId ? value : false };
        }
        return s.status_id === statusId ? { ...s, [field]: value } : s;
      })
    );
  };

  const initialCount = includedStatuses.filter((s) => s.is_initial).length;
  const terminalCount = includedStatuses.filter((s) => s.is_terminal).length;
  const statusesValid = includedStatuses.length > 0 && initialCount === 1;

  const saveStatuses = async () => {
    if (!statusesValid) {
      toast.warning("Select at least one status and mark exactly one as initial.");
      return;
    }
    try {
      setSavingStatuses(true);
      const res = await api.put(`/task-workflows/${id}/statuses`, {
        statuses: includedStatuses.map(({ status_id, display_order, is_initial, is_terminal, is_required }) => ({
          status_id, display_order, is_initial, is_terminal, is_required,
        })),
      });
      setWorkflow(res.data?.data);
      toast.success("Workflow statuses saved");
    } catch (err) {
      console.error("Save workflow statuses error:", err);
      toast.error(err.response?.data?.message || "Failed to save statuses");
    } finally {
      setSavingStatuses(false);
    }
  };

  const addTransitionRow = () => {
    if (transitionEndpointStatuses.length < 2) {
      toast.warning("Add at least 2 selectable (non-legacy) statuses before defining a transition.");
      return;
    }
    setTransitions((prev) => [
      ...prev,
      {
        from_status_id: transitionEndpointStatuses[0].status_id,
        to_status_id: transitionEndpointStatuses[1].status_id,
        transition_code: "",
        transition_name: "",
        required_permission_module: "tasks",
        required_permission_action: "change_status",
        requires_remark: false,
        requires_assignment: false,
        is_reopen_transition: false,
        display_order: prev.length,
      },
    ]);
  };

  const updateTransition = (index, field, value) => {
    setTransitions((prev) => prev.map((t, i) => (i === index ? { ...t, [field]: value } : t)));
  };

  const removeTransition = (index) => {
    setTransitions((prev) => prev.filter((_, i) => i !== index));
  };

  const transitionsValid = useMemo(() => {
    const seen = new Set();
    for (const t of transitions) {
      if (!t.transition_code.trim()) return false;
      if (t.from_status_id === t.to_status_id) return false;
      const key = `${t.from_status_id}:${t.to_status_id}`;
      if (seen.has(key)) return false;
      seen.add(key);
    }
    return true;
  }, [transitions]);

  const saveTransitions = async () => {
    if (!transitionsValid) {
      toast.warning("Fix invalid transitions: each needs a code, cannot go from a status to itself, and no duplicate from/to pairs.");
      return;
    }
    try {
      setSavingTransitions(true);
      const res = await api.put(`/task-workflows/${id}/transitions`, { transitions });
      setWorkflow(res.data?.data);
      toast.success("Workflow transitions saved");
    } catch (err) {
      console.error("Save workflow transitions error:", err);
      toast.error(err.response?.data?.message || "Failed to save transitions");
    } finally {
      setSavingTransitions(false);
    }
  };

  if (loading) {
    return (
      <div className="unit-page workflow-editor-theme">
        <div className="unit-loader">
          <Spinner animation="border" />
          <p>Loading workflow...</p>
        </div>
      </div>
    );
  }

  if (!workflow) {
    return (
      <div className="unit-page workflow-editor-theme">
        <Alert variant="danger" className="unit-alert">Workflow not found.</Alert>
        <Button className="unit-btn unit-btn-light" onClick={() => navigate("/admin/task-workflows")}>
          <i className="fa-solid fa-arrow-left" aria-hidden="true"></i>
          Back
        </Button>
      </div>
    );
  }

  return (
    <div className="unit-page workflow-editor-theme">

      <div className="unit-header">
        <div>
          <div className="unit-page-label">
            <Link to="/admin/task-workflows" className="workflow-editor-back-link">
              <i className="fa-solid fa-arrow-left" aria-hidden="true"></i>
              Workflows
            </Link>
          </div>
          <h2>{workflow.name}</h2>
          <p>
            Code: <code>{workflow.code}</code> · Scope: <Badge bg="light" text="dark">{workflow.scope_type}</Badge>
            {workflow.is_default && <> · <Badge bg="info" className="unit-status">Default</Badge></>}
          </p>
        </div>
      </div>

      <div className="unit-card mb-4">
        <div className="unit-toolbar">
          <div>
            <h5>Statuses in this Workflow</h5>
            <p>Exactly one status must be marked as initial. Multiple terminal statuses are allowed.</p>
          </div>
        </div>

        {!statusesValid && (
          <Alert variant="warning" className="mx-3 unit-alert">
            {includedStatuses.length === 0
              ? "Add at least one status."
              : initialCount === 0
              ? "Mark exactly one status as initial."
              : "Only one status can be marked as initial."}
          </Alert>
        )}
        {terminalCount === 0 && includedStatuses.length > 0 && (
          <Alert variant="secondary" className="mx-3 unit-alert">No terminal status marked yet — consider marking at least one.</Alert>
        )}

        <div className="unit-table-wrapper">
          <Table responsive className="unit-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Order</th>
                <th>Initial</th>
                <th>Terminal</th>
                <th>Required</th>
                <th className="text-end">Remove</th>
              </tr>
            </thead>
            <tbody>
              {includedStatuses.map((s) => (
                <tr key={s.status_id}>
                  <td><strong>{s.name}</strong> <span className="text-muted">({s.code})</span></td>
                  <td style={{ maxWidth: 90 }}>
                    <Form.Control
                      type="number"
                      size="sm"
                      value={s.display_order}
                      onChange={(e) => updateIncludedStatus(s.status_id, "display_order", Number(e.target.value))}
                    />
                  </td>
                  <td>
                    <Form.Check
                      type="radio"
                      name="initial-status"
                      checked={s.is_initial}
                      onChange={() => updateIncludedStatus(s.status_id, "is_initial", true)}
                    />
                  </td>
                  <td>
                    <Form.Check
                      type="checkbox"
                      checked={s.is_terminal}
                      onChange={(e) => updateIncludedStatus(s.status_id, "is_terminal", e.target.checked)}
                    />
                  </td>
                  <td>
                    <Form.Check
                      type="checkbox"
                      checked={s.is_required}
                      onChange={(e) => updateIncludedStatus(s.status_id, "is_required", e.target.checked)}
                    />
                  </td>
                  <td className="text-end">
                    <Button
                      className="unit-icon-btn unit-delete"
                      title="Remove from workflow"
                      aria-label={`Remove ${s.name || "status"} from workflow`}
                      onClick={() => toggleIncludeStatus({ id: s.status_id }, false)}
                    >
                      <i className="fa-solid fa-trash" aria-hidden="true"></i>
                    </Button>
                  </td>
                </tr>
              ))}
              {includedStatuses.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-muted text-center">No statuses added yet.</td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        {availableStatuses.length > 0 && (
          <div className="px-3 pb-3">
            <Form.Label>Add a status</Form.Label>
            <div className="d-flex flex-wrap gap-2">
              {availableStatuses.map((s) => (
                <Button
                  key={s.id}
                  size="sm"
                  className="unit-btn unit-btn-light workflow-editor-add-status-btn"
                  onClick={() => toggleIncludeStatus(s, true)}
                >
                  <i className="fa-solid fa-plus" aria-hidden="true"></i>
                  {s.name}
                </Button>
              ))}
            </div>
          </div>
        )}

        <div className="unit-footer">
          <div />
          <Button className="unit-btn unit-btn-primary" onClick={saveStatuses} disabled={savingStatuses}>
            <i className="fa-solid fa-floppy-disk" aria-hidden="true"></i>
            {savingStatuses ? "Saving..." : "Save Statuses"}
          </Button>
        </div>
      </div>

      <div className="unit-card">
        <div className="unit-toolbar">
          <div>
            <h5>Transitions</h5>
            <p>Only statuses added above can be used as a transition's from/to. Save statuses first.</p>
          </div>
          <div className="unit-toolbar-actions">
            <Button className="unit-btn unit-btn-primary" onClick={addTransitionRow}>
              <i className="fa-solid fa-plus" aria-hidden="true"></i>
              Add Transition
            </Button>
          </div>
        </div>

        <div className="unit-table-wrapper">
          <Table responsive className="unit-table">
            <thead>
              <tr>
                <th>From</th>
                <th>To</th>
                <th>Code</th>
                <th>Name</th>
                <th>Permission Module</th>
                <th>Permission Action</th>
                <th>Remark?</th>
                <th>Assign?</th>
                <th>Reopen?</th>
                <th className="text-end">Remove</th>
              </tr>
            </thead>
            <tbody>
              {transitions.map((t, index) => (
                <tr key={index}>
                  <td>
                    <Form.Select
                      size="sm"
                      value={t.from_status_id}
                      onChange={(e) => updateTransition(index, "from_status_id", Number(e.target.value))}
                    >
                      {transitionEndpointStatuses.map((s) => <option key={s.status_id} value={s.status_id}>{s.name}</option>)}
                    </Form.Select>
                  </td>
                  <td>
                    <Form.Select
                      size="sm"
                      value={t.to_status_id}
                      onChange={(e) => updateTransition(index, "to_status_id", Number(e.target.value))}
                    >
                      {transitionEndpointStatuses.map((s) => <option key={s.status_id} value={s.status_id}>{s.name}</option>)}
                    </Form.Select>
                  </td>
                  <td style={{ minWidth: 130 }}>
                    <Form.Control
                      size="sm"
                      placeholder="e.g. START_WORK"
                      value={t.transition_code}
                      onChange={(e) => updateTransition(index, "transition_code", e.target.value)}
                    />
                  </td>
                  <td style={{ minWidth: 130 }}>
                    <Form.Control
                      size="sm"
                      placeholder="e.g. Start Work"
                      value={t.transition_name}
                      onChange={(e) => updateTransition(index, "transition_name", e.target.value)}
                    />
                  </td>
                  <td style={{ minWidth: 110 }}>
                    <Form.Control
                      size="sm"
                      value={t.required_permission_module}
                      onChange={(e) => updateTransition(index, "required_permission_module", e.target.value)}
                    />
                  </td>
                  <td style={{ minWidth: 110 }}>
                    <Form.Control
                      size="sm"
                      value={t.required_permission_action}
                      onChange={(e) => updateTransition(index, "required_permission_action", e.target.value)}
                    />
                  </td>
                  <td>
                    <Form.Check
                      type="checkbox"
                      checked={t.requires_remark}
                      onChange={(e) => updateTransition(index, "requires_remark", e.target.checked)}
                    />
                  </td>
                  <td>
                    <Form.Check
                      type="checkbox"
                      checked={t.requires_assignment}
                      onChange={(e) => updateTransition(index, "requires_assignment", e.target.checked)}
                    />
                  </td>
                  <td>
                    <Form.Check
                      type="checkbox"
                      checked={t.is_reopen_transition}
                      onChange={(e) => updateTransition(index, "is_reopen_transition", e.target.checked)}
                    />
                  </td>
                  <td className="text-end">
                    <Button
                      className="unit-icon-btn unit-delete"
                      title="Remove transition"
                      aria-label={`Remove transition${t.transition_name ? ` "${t.transition_name}"` : ""}`}
                      onClick={() => removeTransition(index)}
                    >
                      <i className="fa-solid fa-trash" aria-hidden="true"></i>
                    </Button>
                  </td>
                </tr>
              ))}
              {transitions.length === 0 && (
                <tr>
                  <td colSpan="10" className="text-muted text-center">No transitions defined yet.</td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        <div className="unit-footer">
          <div />
          <Button className="unit-btn unit-primary unit-btn-primary" onClick={saveTransitions} disabled={savingTransitions}>
            <i className="fa-solid fa-floppy-disk" aria-hidden="true"></i>
            {savingTransitions ? "Saving..." : "Save Transitions"}
          </Button>
        </div>
      </div>
    </div>
  );
}
